import { Offer } from '../../services/exports/Interfaces';
import { OfferKindEnum } from '../../services/exports/Constants';
import dayjs from 'dayjs';
import useAppMethods from '../../hooks/utility/useAppMethods';
import { useTranslation } from 'react-i18next';

const useOfferModalTranslation = (offer: Offer) => {
  const { formatCurrency } = useAppMethods();
  const { t, i18n } = useTranslation(null, { keyPrefix: 'Modals:OfferModal' });
  const startsAt = dayjs(offer.starts_at).format('HH:mm');
  const endsAt = dayjs(offer.ends_at).format('HH:mm');
  const fromTo = offer.is_happy_hour
    ? t('from_to', {
        startsAt,
        endsAt,
      })
    : '';
  const defaultDescriptionOpts = {
    fromTo,
  };
  const getTitleByOffer = (offer: Offer): string => {
    if (offer.translations?.[i18n.language]?.title) {
      return offer.translations[i18n.language].title;
    }

    return {
      [OfferKindEnum.PercentageOffByProducts]: t(`offers.${OfferKindEnum.PercentageOffByProducts}.title`, {
        percentageAmount: offer.details.percentage_amount,
      }),
      [OfferKindEnum.PercentageOffByMinSpendValue]: t(`offers.${OfferKindEnum.PercentageOffByMinSpendValue}.title`, {
        minSpendValue: formatCurrency(offer.details.min_spend_value),
        percentageAmount: offer.details.percentage_amount,
      }),
      [OfferKindEnum.AmountOffByProducts]: t(`offers.${OfferKindEnum.AmountOffByProducts}.title`, {
        amount: formatCurrency(offer.details.amount),
      }),
      [OfferKindEnum.AmountOffByMinSpendValue]: t(`offers.${OfferKindEnum.AmountOffByMinSpendValue}.title`, {
        amount: formatCurrency(offer.details.amount),
        minSpendValue: formatCurrency(offer.details.min_spend_value),
      }),
      [OfferKindEnum.Bogo]: t(`offers.${OfferKindEnum.Bogo}.title`),
      [OfferKindEnum.BogoHalfPrice]: t(`offers.${OfferKindEnum.BogoHalfPrice}.title`),
      [OfferKindEnum.FreeItemByProducts]: t(`offers.${OfferKindEnum.FreeItemByProducts}.title`),
      [OfferKindEnum.FreeItemByMinSpendValue]: t(`offers.${OfferKindEnum.FreeItemByMinSpendValue}.title`, {
        minSpendValue: formatCurrency(offer.details.min_spend_value),
      }),
    }[offer.kind];
  };

  const getDescriptionByOffer = (offer: Offer): string => {
    if (offer.translations?.[i18n.language]?.description) {
      return offer.translations[i18n.language].description;
    }

    return {
      [OfferKindEnum.PercentageOffByProducts]: t(`offers.${OfferKindEnum.PercentageOffByProducts}.description`, {
        ...defaultDescriptionOpts,
        percentageAmount: offer.details.percentage_amount,
      }),
      [OfferKindEnum.PercentageOffByMinSpendValue]: t(
        `offers.${OfferKindEnum.PercentageOffByMinSpendValue}.description`,
        {
          ...defaultDescriptionOpts,
          minSpendValue: formatCurrency(offer.details.min_spend_value),
          percentageAmount: offer.details.percentage_amount,
        },
      ),
      [OfferKindEnum.AmountOffByProducts]: t(`offers.${OfferKindEnum.AmountOffByProducts}.description`, {
        ...defaultDescriptionOpts,
        amount: formatCurrency(offer.details.amount),
      }),
      [OfferKindEnum.AmountOffByMinSpendValue]: t(`offers.${OfferKindEnum.AmountOffByMinSpendValue}.description`, {
        ...defaultDescriptionOpts,
        amount: formatCurrency(offer.details.amount),
        minSpendValue: formatCurrency(offer.details.min_spend_value),
      }),
      [OfferKindEnum.Bogo]: t(`offers.${OfferKindEnum.Bogo}.description`, defaultDescriptionOpts),
      [OfferKindEnum.BogoHalfPrice]: t(`offers.${OfferKindEnum.BogoHalfPrice}.description`, defaultDescriptionOpts),
      [OfferKindEnum.FreeItemByProducts]: t(
        `offers.${OfferKindEnum.FreeItemByProducts}.description`,
        defaultDescriptionOpts,
      ),
      [OfferKindEnum.FreeItemByMinSpendValue]: t(`offers.${OfferKindEnum.FreeItemByMinSpendValue}.description`, {
        ...defaultDescriptionOpts,
        minSpendValue: formatCurrency(offer.details.min_spend_value),
      }),
    }[offer.kind];
  };

  return { getDescriptionByOffer, getTitleByOffer };
};

export default useOfferModalTranslation;
